import { useCallback, useId, useRef, useState } from 'react';
import { Box, Button, FormHelperText, IconButton, Stack, Typography } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

const DEFAULT_MAX_BYTES = 10 * 1024 * 1024;
const PDF_MAGIC = [0x25, 0x50, 0x44, 0x46];

export type FileUploadError =
  | { kind: 'too-large'; sizeBytes: number; maxBytes: number }
  | { kind: 'wrong-mime'; mime: string; acceptedMimes: string[] }
  | { kind: 'magic-mismatch'; declaredMime: string };

export interface FileUploadProps {
  value: File | null;
  onChange: (file: File | null, error: FileUploadError | null) => void;
  /**
   * MIME types aceitos. Default: `["application/pdf"]`. Para qualquer arquivo,
   * passar lista vazia (validação MIME pulada).
   */
  acceptedMimes?: string[];
  /** Default 10 MB. */
  maxBytes?: number;
  /** Checa primeiros 4 bytes do arquivo p/ PDF. Default true se aceitar `application/pdf`. */
  checkPdfMagic?: boolean;
  label?: string;
  helperText?: string;
  disabled?: boolean;
}

function readMagic(file: File, byteCount: number): Promise<Uint8Array> {
  // Usa FileReader (compatível com jsdom) em vez de Blob#arrayBuffer (instável
  // em jsdom 25). `byteCount` é pequeno (4 bytes para PDF) — custo desprezível.
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const buf = reader.result as ArrayBuffer;
      resolve(new Uint8Array(buf).slice(0, byteCount));
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsArrayBuffer(file.slice(0, byteCount));
  });
}

export function FileUpload({
  value,
  onChange,
  acceptedMimes = ['application/pdf'],
  maxBytes = DEFAULT_MAX_BYTES,
  checkPdfMagic,
  label,
  helperText,
  disabled = false,
}: FileUploadProps): JSX.Element {
  const { t } = useTranslation();
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const shouldCheckPdf = checkPdfMagic ?? acceptedMimes.includes('application/pdf');

  const handle = useCallback(
    async (file: File | undefined) => {
      if (!file) {
        onChange(null, null);
        return;
      }
      if (file.size > maxBytes) {
        onChange(null, { kind: 'too-large', sizeBytes: file.size, maxBytes });
        return;
      }
      if (acceptedMimes.length > 0 && !acceptedMimes.includes(file.type)) {
        onChange(null, { kind: 'wrong-mime', mime: file.type, acceptedMimes });
        return;
      }
      if (shouldCheckPdf && file.type === 'application/pdf') {
        setBusy(true);
        try {
          const magic = await readMagic(file, PDF_MAGIC.length);
          const ok = PDF_MAGIC.every((b, i) => magic[i] === b);
          if (!ok) {
            onChange(null, { kind: 'magic-mismatch', declaredMime: file.type });
            return;
          }
        } finally {
          setBusy(false);
        }
      }
      onChange(file, null);
    },
    [acceptedMimes, maxBytes, onChange, shouldCheckPdf],
  );

  return (
    <Box>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={acceptedMimes.length > 0 ? acceptedMimes.join(',') : undefined}
        disabled={disabled || busy}
        onChange={(e) => void handle(e.target.files?.[0])}
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
        aria-label={label ?? t('components.fileUpload.label')}
      />
      <Stack direction="row" spacing={2} alignItems="center">
        <Button
          variant="outlined"
          startIcon={<CloudUploadIcon />}
          disabled={disabled || busy}
          onClick={() => inputRef.current?.click()}
        >
          {label ?? t('components.fileUpload.label')}
        </Button>
        {value && (
          <>
            <Typography variant="body2" sx={{ flex: 1 }} noWrap title={value.name}>
              {value.name} ({(value.size / 1024).toFixed(0)} KB)
            </Typography>
            <IconButton
              size="small"
              aria-label={t('components.fileUpload.remove')}
              onClick={() => {
                if (inputRef.current) inputRef.current.value = '';
                onChange(null, null);
              }}
              disabled={disabled || busy}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        )}
      </Stack>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </Box>
  );
}

/**
 * Devolve a chave i18n e o objeto de interpolação correspondente ao erro.
 * O caller faz `t(key, values)` direto — evita ter que tipar `TFunction` aqui
 * (signature complexa do react-i18next causava double-cast `as unknown as`).
 */
export function fileUploadErrorTranslation(error: FileUploadError): {
  key: string;
  values?: Record<string, unknown>;
} {
  switch (error.kind) {
    case 'too-large':
      return {
        key: 'components.fileUpload.errors.tooLarge',
        values: { maxMb: Math.round(error.maxBytes / 1024 / 1024) },
      };
    case 'wrong-mime':
      return {
        key: 'components.fileUpload.errors.wrongMime',
        values: { accepted: error.acceptedMimes.join(', ') },
      };
    case 'magic-mismatch':
      return { key: 'components.fileUpload.errors.magicMismatch' };
  }
}
